import type {
    IAmountTick,
    ICancellationReasonByIdentitySummary,
    ICancellationReasonSummary,
    ICancellationUserInfo,
    ICancellationsSummary,
    IDashboardStatsSummary,
    IDateRange,
    IKpiSummary,
    ISalesByApplicationSummary,
    ISalesByOfferCategorySummary,
    ISalesByPaymentMethodSummary,
    ISalesByReportingCategorySummary,
    ISalesForPaymentMethodSummary,
    ISalesSummary,
} from "@/data/model/Dashboard";
import { DashboardStatsPeriod } from "@/data/model/Enums";
import { DateTime } from "@/data/util/DateTime";

import { BaseServiceDao, parseCollection } from "./BaseServiceDao";

export class DashboardDao extends BaseServiceDao {
    public static className = "DashboardDao";

    constructor() {
        super("dashboard");
    }

    public getMonthlyTurnover(): Promise<Array<IAmountTick>> {
        return this.get("stats/monthlyturnover", undefined, (c) => parseCollection(c, DashboardDao.parseAmountTick));
    }

    public getMonthlySignups(): Promise<Array<IAmountTick>> {
        return this.get("stats/monthlysignup", undefined, (c) => parseCollection(c, DashboardDao.parseAmountTick));
    }

    public getDailyAllocations(): Promise<Array<IAmountTick>> {
        return this.get("stats/dailyallocation", undefined, (c) => parseCollection(c, DashboardDao.parseAmountTick));
    }

    public getYearlyOrders(): Promise<Array<IAmountTick>> {
        return this.get("stats/yearlyorder", undefined, (c) => parseCollection(c, DashboardDao.parseAmountTick));
    }

    public getSalesSummary(locationId: string, period: DashboardStatsPeriod): Promise<ISalesSummary> {
        return this.get(
            `stats/${locationId}/sales/${DashboardStatsPeriod[period]}`,
            undefined,
            DashboardDao.parseSalesSummary
        );
    }

    public getSalesByApplicationSummary(locationId: string, period: DashboardStatsPeriod): Promise<ISalesSummary> {
        return this.get(
            `stats/${locationId}/sales-by-application/${DashboardStatsPeriod[period]}`,
            undefined,
            DashboardDao.parseSalesSummary
        );
    }

    public getSalesByOfferCategorySummary(
        locationId: string,
        period: DashboardStatsPeriod
    ): Promise<ISalesByOfferCategorySummary> {
        return this.get(
            `stats/${locationId}/sales-by-offer-category/${DashboardStatsPeriod[period]}`,
            undefined,
            DashboardDao.parseSalesByOfferCategorySummary
        );
    }

    public getSalesByPaymentMethodSummary(
        locationId: string,
        period: DashboardStatsPeriod
    ): Promise<ISalesByPaymentMethodSummary> {
        return this.get(
            `stats/${locationId}/sales-by-payment-method/${DashboardStatsPeriod[period]}`,
            undefined,
            DashboardDao.parseSalesByPaymentMethodSummary
        );
    }

    public getSalesByReportingCategorySummary(
        locationId: string,
        period: DashboardStatsPeriod
    ): Promise<ISalesByReportingCategorySummary> {
        return this.get(
            `stats/${locationId}/sales-by-reporting-category/${DashboardStatsPeriod[period]}`,
            undefined,
            DashboardDao.parseSalesByReportingCategorySummary
        );
    }

    public getCancellationsSummary(locationId: string, period: DashboardStatsPeriod): Promise<ICancellationsSummary> {
        return this.get(
            `stats/${locationId}/cancellations/${DashboardStatsPeriod[period]}`,
            undefined,
            DashboardDao.parseCancellationsSummary
        );
    }

    public getKpiSummary(locationId: string, period: DashboardStatsPeriod): Promise<IKpiSummary> {
        return this.get(
            `stats/${locationId}/kpi/${DashboardStatsPeriod[period]}`,
            undefined,
            DashboardDao.parseKpiSummary
        );
    }

    public static parseSalesSummary(literal: any): ISalesSummary {
        return {
            ...DashboardDao.parseStatsSummary(literal),
            totalAmount: parseFloat(literal.totalAmount),
            orders: parseInt(literal.orders),
            payments: parseInt(literal.payments),
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public static parseSalesByApplicationSummary(literal: any): ISalesByApplicationSummary {
        return {};
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public static parseSalesByOfferCategorySummary(literal: any): ISalesByOfferCategorySummary {
        return {};
    }

    public static parseSalesByPaymentMethodSummary(literal: any): ISalesByPaymentMethodSummary {
        return {
            dateRange: DashboardDao.parseDateRange(literal.dateRange),
            onlineBrowserPaymentMethodSummaries: parseCollection(literal.onlineBrowserPaymentMethodSummaries, (c) =>
                DashboardDao.parseSalesForPaymentMethodSummary(c)
            ),
            onlinePaymentDevicePaymentMethodSummaries: parseCollection(
                literal.onlinePaymentDevicePaymentMethodSummaries,
                (c) => DashboardDao.parseSalesForPaymentMethodSummary(c)
            ),
            offlinePaymentMethodSummaries: parseCollection(literal.offlinePaymentMethodSummaries, (c) =>
                DashboardDao.parseSalesForPaymentMethodSummary(c)
            ),
        };
    }

    public static parseSalesForPaymentMethodSummary(literal: any): ISalesForPaymentMethodSummary {
        return {
            paymentMethodName: literal.paymentMethodName,
            totalAmount: parseFloat(literal.totalAmount),
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public static parseSalesByReportingCategorySummary(literal: any): ISalesByReportingCategorySummary {
        return {};
    }

    public static parseCancellationsSummary(literal: any): ICancellationsSummary {
        return {
            dateRange: DashboardDao.parseDateRange(literal.dateRange),
            cancellationReasonSummaries: parseCollection(literal.cancellationReasonSummaries, (c) =>
                DashboardDao.parseCancellationReasonSummary(c)
            ),
            cancellationUsers: parseCollection(literal.cancellationUsers, (c) =>
                DashboardDao.parseCancellationUserInfo(c)
            ),
        };
    }

    public static parseCancellationReasonSummary(literal: any): ICancellationReasonSummary {
        return {
            reason: literal.reason != null ? parseInt(literal.reason) : undefined,
            amount: parseFloat(literal.amount),
            summariesByIdentity: parseCollection(literal.summariesByIdentity, (c) =>
                DashboardDao.parseCancellationSummaryByIdentity(c)
            ),
        };
    }

    public static parseCancellationSummaryByIdentity(literal: any): ICancellationReasonByIdentitySummary {
        return {
            identityId: literal.identityId,
            userName: literal.userName,
            amount: parseFloat(literal.amount),
        };
    }

    public static parseCancellationUserInfo(literal: any): ICancellationUserInfo {
        return {
            identityId: literal.identityId,
            userName: literal.userName,
            amount: parseFloat(literal.amount),
        };
    }

    public static parseKpiSummary(literal: any): IKpiSummary {
        return {
            netSalesExclTip: parseFloat(literal.netSalesExclTip),
            avgOrder: literal.avgOrder != null ? parseFloat(literal.avgOrder) : undefined,
            avgPayment: literal.avgPayment != null ? parseFloat(literal.avgPayment) : undefined,
            avgBill: literal.avgBill != null ? parseFloat(literal.avgBill) : undefined,
            avgBillDurationMins:
                literal.avgBillDurationMins != null ? parseInt(literal.avgBillDurationMins) : undefined,
        };
    }

    public static parseStatsSummary(literal: any): IDashboardStatsSummary {
        return {
            granularity: parseInt(literal.granularity),
            dateRanges: parseCollection(literal.dateRanges, (c) => DashboardDao.parseDateRange(c)),
            data: parseCollection(literal.data, (c) => DashboardDao.parseAmountTick(c)),
        };
    }

    public static parseAmountTick(literal: any): IAmountTick {
        return {
            amount: parseInt(literal.amount),
            dateRange: DashboardDao.parseDateRange(literal.dateRange),
        };
    }

    public static parseDateRange(literal: any): IDateRange {
        return {
            from: DateTime.parseIsoDate(literal.from),
            to: DateTime.parseIsoDate(literal.to),
        };
    }
}
